import {
  useReactTable,
  getCoreRowModel,
  getGroupedRowModel,
  getExpandedRowModel,
  flexRender,
  type ColumnDef,
  type GroupingState,
} from '@tanstack/react-table';
import { useState } from 'react';
import { ChevronRight, ChevronDown } from 'lucide-react';

interface DataTableProps<T> {
  data: T[];
  columns: ColumnDef<T, any>[];
  grouping?: string[];
  isLoading?: boolean;
  shimmerLines?: number;
}

export function DataTable<T>({
  data,
  columns,
  grouping: initialGrouping = [],
  isLoading = false,
  shimmerLines = 5,
}: DataTableProps<T>) {
  const [grouping] = useState<GroupingState>(initialGrouping);

  const table = useReactTable({
    data,
    columns,
    state: { grouping },
    getCoreRowModel: getCoreRowModel(),
    getGroupedRowModel: initialGrouping.length > 0 ? getGroupedRowModel() : undefined,
    getExpandedRowModel: initialGrouping.length > 0 ? getExpandedRowModel() : undefined,
  });

  if (isLoading) {
    return (
      <div className="w-full">
        <div className="border-b border-gray-200 pb-2 mb-2">
          <div className="flex gap-4">
            {columns.map((_, i) => (
              <div key={i} className="h-4 bg-gray-200 rounded animate-pulse flex-1" />
            ))}
          </div>
        </div>
        {Array.from({ length: shimmerLines }).map((_, i) => (
          <div key={i} className="flex gap-4 py-3">
            {columns.map((_, j) => (
              <div key={j} className="h-4 bg-gray-100 rounded animate-pulse flex-1" />
            ))}
          </div>
        ))}
      </div>
    );
  }

  return (
    <table className="w-full text-sm text-left">
      <thead>
        {table.getHeaderGroups().map((headerGroup) => (
          <tr key={headerGroup.id} className="border-b border-gray-200">
            {headerGroup.headers.map((header) => (
              <th
                key={header.id}
                className="px-3 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider"
              >
                {header.isPlaceholder
                  ? null
                  : flexRender(header.column.columnDef.header, header.getContext())}
              </th>
            ))}
          </tr>
        ))}
      </thead>
      <tbody className="divide-y divide-gray-100">
        {table.getRowModel().rows.map((row) => {
          if (row.getIsGrouped()) {
            return (
              <tr key={row.id}>
                <td
                  colSpan={columns.length}
                  className="px-3 py-2 bg-gray-50 font-semibold text-sm text-gray-800 cursor-pointer select-none"
                  onClick={() => row.toggleExpanded()}
                >
                  <span className="inline-flex items-center gap-1">
                    {row.getIsExpanded() ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                    {String(row.groupingValue)} ({row.subRows.length})
                  </span>
                </td>
              </tr>
            );
          }
          return (
            <tr key={row.id} className="hover:bg-gray-50">
              {row.getVisibleCells().map((cell) => (
                <td key={cell.id} className="px-3 py-3 text-gray-700">
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
